import * as React from 'react';
import { flexRender } from '@tanstack/react-table';
import { Loader2Icon, PinIcon, PinOffIcon } from 'lucide-react';
import type { Cell, ColumnDef, Header } from '@tanstack/react-table';
import type { Column, Table as TanTable } from '@tanstack/table-core';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { TableActions, TablePagination } from './table-ui';
import { type TableRef, useTableWithRef } from './use-table';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

export const getCommonPinningStyles = <TData,>(column: Column<TData, unknown>): React.CSSProperties => {
  const isPinned = column.getIsPinned();
  const isFirstRightPinnedColumn = isPinned === 'right' && column.getIsFirstColumn('right');
  const isLastLeftPinnedColumn = isPinned === 'left' && column.getIsLastColumn('left');
  const leftPosition = column.getStart('left');
  const rightPosition = column.getAfter('right');

  return {
    zIndex: isPinned ? 40 : 0,
    position: isPinned ? 'sticky' : 'relative',
    left: isPinned === 'left' ? `${leftPosition}px` : undefined,
    right: isPinned === 'right' ? `${rightPosition}px` : undefined,
    borderRight: isLastLeftPinnedColumn ? '1px solid var(--color-gray-100)' : undefined,
    borderLeft: isFirstRightPinnedColumn ? '1px solid var(--color-gray-100)' : undefined,
  };
};

export type SimpleTableProps<TData> = {
  data: TData[];
  columns: ColumnDef<TData>[];
  getRowId: (row: TData) => string;
  getRowClassName?: (row: TData) => string;
  onRowClick?: (row: TData) => void;
  actions?: (table: TanTable<TData>) => React.ReactNode;
  isLoading?: boolean;
  isRefreshing?: boolean;
  enableColumnResizing?: boolean;
};

export interface SimpleTableRef<TData> extends TableRef<TData> {}

// Component for handling pinned table cells
interface TablePinnedCellProps<TData> {
  cell: Cell<TData, unknown>;
}

// Component for handling pinned table headers
interface TablePinnedHeadProps<TData> {
  enableColumnPinning: boolean;
  header: Header<TData, unknown>;
}

export function TablePinnedCell<TData>({ cell }: TablePinnedCellProps<TData>) {
  const isPinned = cell.column.getIsPinned();

  return (
    <TableCell
      key={cell.id}
      style={getCommonPinningStyles(cell.column)}
      className={cn(
        // Base background for pinned cells that respects row state
        isPinned && 'bg-background',
        // Use parent row hover state for pinned cells
        isPinned && '[tr:hover_&]:bg-gray-50',
        // Use parent row selected state for pinned cells
        isPinned && '[tr[data-state=selected]_&]:bg-gray-50',
        // isLastLeftPinnedColumn && 'border-r border-muted',
        // isFirstRightPinnedColumn && 'border-l border-muted',
      )}>
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </TableCell>
  );
}

export function TablePinnedHead<TData>({ header, enableColumnPinning }: TablePinnedHeadProps<TData>) {
  const isPinned = header.column.getIsPinned();
  const isFirstRightPinnedColumn = isPinned === 'right' && header.column.getIsFirstColumn('right');
  const isLastLeftPinnedColumn = isPinned === 'left' && header.column.getIsLastColumn('left');

  return (
    <TableHead
      key={header.id}
      colSpan={header.colSpan}
      className={cn(isPinned && 'bg-muted z-40', !isPinned && 'z-0')}
      style={{
        ...getCommonPinningStyles(header.column),
        width: header.column.columnDef.size,
        minWidth: header.column.columnDef.minSize,
        maxWidth: header.column.columnDef.maxSize,
      }}>
      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}

      {/* Column Pinning Controls */}
      {enableColumnPinning && !header.isPlaceholder && header.column.getCanPin() && (
        <div className="flex items-center gap-1">
          {header.column.getIsPinned() !== 'left' && (
            <Button
              size="sm"
              variant="ghost"
              title="Pin to left"
              className="h-6 w-6 p-0"
              onClick={() => header.column.pin('left')}>
              <PinIcon className="h-3 w-3 rotate-45" />
            </Button>
          )}
          {header.column.getIsPinned() && (
            <Button
              size="sm"
              variant="ghost"
              title="Unpin column"
              className="h-6 w-6 p-0"
              onClick={() => header.column.pin(false)}>
              <PinOffIcon className="h-3 w-3" />
            </Button>
          )}
          {header.column.getIsPinned() !== 'right' && (
            <Button
              size="sm"
              variant="ghost"
              title="Pin to right"
              className="h-6 w-6 p-0"
              onClick={() => header.column.pin('right')}>
              <PinIcon className="h-3 w-3 -rotate-45" />
            </Button>
          )}
        </div>
      )}
    </TableHead>
  );
}

export const SimpleTable = React.forwardRef<SimpleTableRef<any>, SimpleTableProps<any>>(function SimpleTable<TData>(
  {
    data,
    columns,
    actions,
    getRowId,
    getRowClassName,
    onRowClick,
    isLoading = false,
    isRefreshing = false,
    enableColumnResizing = false,
  }: SimpleTableProps<TData>,
  ref: React.Ref<SimpleTableRef<TData>>,
) {
  const { table } = useTableWithRef(
    {
      data,
      columns,
      getRowId,
      enableColumnResizing,
    },
    ref,
  );

  return (
    <div className="flex flex-col gap-2">
      <TableActions table={table} actions={actions} />

      <div className={cn('relative rounded-lg border bg-background overflow-hidden', isRefreshing && 'opacity-70')}>
        <Table className="w-full">
          <TableHeader className="bg-muted/40">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent border-b">
                {headerGroup.headers.map((header) => (
                  <TablePinnedHead key={header.id} header={header} enableColumnPinning={false} />
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <Loader2Icon className="size-4 animate-spin" />
                    <span className="text-muted-foreground">Loading...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : table.getRowCount() > 0 ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className={cn(getRowClassName?.(row.original), onRowClick && 'cursor-pointer')}
                  onClick={() => onRowClick?.(row.original)}
                  data-state={row.getIsSelected() && 'selected'}>
                  {row.getVisibleCells().map((cell) => (
                    <TablePinnedCell key={cell.id} cell={cell} />
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {table.getRowCount() > 10 && <TablePagination table={table} />}
    </div>
  );
}) as <TData>(props: SimpleTableProps<TData> & { ref?: React.Ref<SimpleTableRef<TData>> }) => React.ReactElement;
